/**
 * PATH: packages/ui/src/kanban/components/card-detail/drawer-sections/CardTabLeftovers.tsx
 * FILE_ID: UI-KANBAN-CARD-TAB-LEFTOVERS-001
 * PURPOSE: Build hideable Card-tab leftover nodes (files through activity).
 * ROLE IN ARCHITECTURE: Catalog render map. CardTabContent stays the frame.
 * MAIN EXPORTS: renderCardTabLeftovers
 * NON-RESPONSIBILITIES: Does not persist. Hide omits chrome, not card data.
 * NOTES FOR FUTURE AI: Self-hide when the card has no data. Do not delete mail.
 */
import type { ReactNode } from 'react'
import {
  Activity as ActivityIcon,
  Calendar,
  CheckSquare,
  Layers,
  Link2,
  Loader2,
  Merge,
  ShieldAlert,
  Tag as TagIcon,
  Users,
} from 'lucide-react'

import { InPageFindBar } from '../../../../components/find/InPageFindBar'
import type { CardTabBlockId } from './cardTabLayout'
import { CardFilesRail } from '../CardFilesRail'
import { TrelloCardDetailSection } from '../../TrelloCardDetailSection'
import { QuoteCardActions } from '../../QuoteCardActions'
import { SubChainsPanel } from '../../SubChainsPanel'
import { CardCashFlowSection } from '../../CardCashFlowSection'
import { CardBlockersSection } from '../CardBlockersSection'
import { MembersSection } from '../MembersSection'
import { RestrictedAccessSection, SubCardsSection } from '../RestrictedAccessSection'
import { TagsSection } from '../TagsSection'
import { ChecklistsSection } from '../checklists'
import { CardEntriesSection } from '../entries/CardEntriesSection'
import { CardWorkbookSection } from './CardWorkbookSection'
import { CardPropertyList } from './CardPropertyList'
import { CardPlacementsSection } from './CardPlacementsSection'
import { CardActivitySection } from './CardActivitySection'
import { CollapsibleSection } from './CollapsibleSection'
import { CardMergedInSection } from '../CardMergedInSection'
import type { CardTabContentProps } from './cardTabContentTypes'
import type { DrawerSectionId } from './drawerSectionPrefs'

export function renderCardTabLeftovers(
  props: CardTabContentProps,
  sectionPrefs: {
    isExpanded: (id: DrawerSectionId) => boolean
    toggle: (id: DrawerSectionId) => void
  },
): Partial<Record<CardTabBlockId, ReactNode>> {
  const {
    card, collabCard, lanes, members, tags, api, boardId, boardCards, orgId,
    cashFlowEnabled, emailAdapter, renderLinks, onChanged, onTagCreated,
    onSelectCards, onConvertToNotebook, onOpenCardId, onOpenEntryCardId,
    onOpenWorkbook, onOpenBoard, allLog, safeActiveTab, setActiveTab,
    findBarProps, editing, data,
  } = props
  const { handleMove, busyKey } = editing
  const {
    qc, scheduleCtxQ, snapMutation, similarQ,
    setMergePreselectId, setMergeDialogOpen, onCollabChanged,
  } = data
  const activeLabelCount = (collabCard.tags ?? []).filter((t) => !t.revoked_at).length
  const memberCount = (collabCard.assigned_user_ids ?? []).length
  const checklistCount =
    card.checklist_summary && card.checklist_summary.total > 0
      ? `${card.checklist_summary.done}/${card.checklist_summary.total}`
      : undefined

  return {
    find: safeActiveTab === 'card' ? <InPageFindBar {...findBarProps} /> : null,
    files: emailAdapter ? (
      <CardFilesRail
        card={card}
        emailAdapter={emailAdapter}
        variant="rail"
        onOpenFiles={() => setActiveTab('files')}
      />
    ) : null,
    merged: <CardMergedInSection card={card} onOpenCardId={onOpenCardId} />,
    workbook: onOpenWorkbook ? (
      <CardWorkbookSection card={card} onOpenWorkbook={onOpenWorkbook} />
    ) : null,
    // ENTRIES SLOT (ARC-3 owns contents; ARC-4 owns placement).
    entries: api.createCardEntries ? (
      <CollapsibleSection
        id="entries"
        title="Entries"
        expanded={sectionPrefs.isExpanded('entries')}
        onToggle={sectionPrefs.toggle}
      >
        <CardEntriesSection
          card={card}
          api={api}
          qc={qc}
          boardId={boardId}
          members={members}
          onOpenCardId={onOpenEntryCardId ?? onOpenCardId}
        />
      </CollapsibleSection>
    ) : null,
    trello: card.provider === 'trello' && card.external_id ? (
      <TrelloCardDetailSection
        externalId={card.external_id}
        externalUrl={card.external_url ?? null}
        api={api}
      />
    ) : null,
    properties: <CardPropertyList card={card} lanes={lanes} editing={editing} />,
    placements: api.addCardPlacement && api.removeCardPlacement ? (
      <CollapsibleSection
        id="placements"
        title="Boards & lanes"
        count={1 + (card.lane_placements?.length ?? 0)}
        icon={<Link2 className="h-3.5 w-3.5" />}
        expanded={sectionPrefs.isExpanded('placements')}
        onToggle={sectionPrefs.toggle}
      >
        <CardPlacementsSection card={card} api={api} boardId={boardId} lanes={lanes} onOpenBoard={onOpenBoard} />
      </CollapsibleSection>
    ) : null,
    quote: (
      <QuoteCardActions
        card={card}
        api={api}
        lanes={lanes}
        onMove={handleMove}
        moveBusy={busyKey === 'lane'}
        onConverted={() => {
          void qc.invalidateQueries({
            queryKey: boardId ? ['kanban', 'cards', boardId] : ['kanban'],
          })
        }}
      />
    ),
    schedule: api.getCardScheduleContext && card.job_id && scheduleCtxQ.data?.job ? (
      <section className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/40 px-3 py-2.5">
        {scheduleCtxQ.data.drift?.is_drifting ? (
          <div className="space-y-1.5">
            <p className="flex items-center gap-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
              <ShieldAlert className="h-3 w-3" />
              Schedule drifts from parent job
            </p>
            {api.snapCardToJob && (
              <button
                type="button"
                onClick={() => snapMutation.mutate(card.id)}
                disabled={snapMutation.isPending}
                className="inline-flex items-center gap-1 rounded-md border border-amber-300 dark:border-amber-700 bg-white dark:bg-neutral-900 px-2 py-1 text-[11px] font-medium text-amber-700 dark:text-amber-300"
              >
                {snapMutation.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                Snap to job dates
              </button>
            )}
          </div>
        ) : (
          <p className="flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400">
            <Calendar className="h-3 w-3" />
            Within job window
          </p>
        )}
      </section>
    ) : null,
    softmatch: api.getSimilarCards && (similarQ.data?.results?.length ?? 0) > 0 ? (
      <section className="rounded-lg border border-violet-200 dark:border-violet-900 bg-violet-50 dark:bg-violet-950/30 px-3 py-2.5">
        <p className="mb-1 flex items-center gap-1 text-[11px] font-semibold text-violet-700 dark:text-violet-300">
          <Merge className="h-3 w-3" />
          Possibly the same as…
        </p>
        <ul className="space-y-1">
          {(similarQ.data?.results ?? []).map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => {
                  if (!api.mergeCards || !boardCards) return
                  setMergePreselectId(s.id)
                  setMergeDialogOpen(true)
                }}
                className="w-full rounded px-2 py-1 text-left text-[11.5px] text-violet-800 dark:text-violet-200"
              >
                {s.title || '(untitled)'}
              </button>
            </li>
          ))}
        </ul>
      </section>
    ) : null,
    subchains: card?.id ? (
      <SubChainsPanel
        api={api}
        cardId={card.id}
        onSelectCards={onSelectCards}
        onConvertToNotebook={onConvertToNotebook}
      />
    ) : null,
    cashflow: cashFlowEnabled ? (
      <CardCashFlowSection
        card={card}
        api={api}
        boardId={boardId}
        boardCards={boardCards}
        onChanged={onChanged}
      />
    ) : null,
    blockers: !cashFlowEnabled ? (
      <CardBlockersSection card={card} api={api} boardId={boardId} boardCards={boardCards} />
    ) : null,
    restricted: members && api.restrictCardTo ? (
      <RestrictedAccessSection card={card} members={members} api={api} onChanged={onChanged} />
    ) : null,
    members: members ? (
      <CollapsibleSection
        id="members"
        title="Members"
        count={memberCount}
        icon={<Users className="h-3.5 w-3.5" />}
        expanded={sectionPrefs.isExpanded('members')}
        onToggle={sectionPrefs.toggle}
      >
        <MembersSection hideTitle card={collabCard} members={members} api={api} onChanged={onCollabChanged} />
      </CollapsibleSection>
    ) : null,
    labels: tags ? (
      <CollapsibleSection
        id="labels"
        title="Labels"
        count={activeLabelCount}
        icon={<TagIcon className="h-3.5 w-3.5" />}
        expanded={sectionPrefs.isExpanded('labels')}
        onToggle={sectionPrefs.toggle}
      >
        <TagsSection
          hideTitle
          card={collabCard}
          orgTags={tags}
          api={api}
          onChanged={onCollabChanged}
          onTagCreated={onTagCreated}
          orgId={orgId}
        />
      </CollapsibleSection>
    ) : null,
    checklists: (
      <CollapsibleSection
        id="checklists"
        title="Checklists"
        count={checklistCount}
        icon={<CheckSquare className="h-3.5 w-3.5" />}
        expanded={sectionPrefs.isExpanded('checklists')}
        onToggle={sectionPrefs.toggle}
      >
        <ChecklistsSection card={card} api={api} qc={qc} boardId={boardId} hideTitle />
      </CollapsibleSection>
    ),
    subcards: api.setCardParent && (card.child_count ?? 0) > 0 ? (
      <CollapsibleSection
        id="subcards"
        title="Sub-cards"
        count={card.child_count}
        icon={<Layers className="h-3.5 w-3.5" />}
        expanded={sectionPrefs.isExpanded('subcards')}
        onToggle={sectionPrefs.toggle}
      >
        <SubCardsSection card={card} api={api} qc={qc} boardId={boardId} hideTitle />
      </CollapsibleSection>
    ) : null,
    linked: renderLinks ? (
      <CollapsibleSection
        id="linked"
        title="Linked"
        icon={<Link2 className="h-3.5 w-3.5" />}
        expanded={sectionPrefs.isExpanded('linked')}
        onToggle={sectionPrefs.toggle}
      >
        <div className="flex flex-wrap gap-1.5">{renderLinks(card)}</div>
      </CollapsibleSection>
    ) : null,
    activity: (
      <CollapsibleSection
        id="activity"
        title="Activity"
        count={allLog.length}
        icon={<ActivityIcon className="h-3.5 w-3.5" />}
        expanded={sectionPrefs.isExpanded('activity')}
        onToggle={sectionPrefs.toggle}
      >
        <CardActivitySection card={card} api={api} allLog={allLog} editing={editing} data={data} onOpenCardId={onOpenCardId} hideTitle />
      </CollapsibleSection>
    ),
  }
}
